import React, { useState } from "react";
import { motion } from "framer-motion";
import { Progress } from "../components/ui/progress";
import ShareStatsCard from "../components/ShareStatsCard";
import ShareableInfographics from "../components/ShareableInfographics";

const FinancialValuesQuiz = ({ theme, goToInput }) => {
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  const [showResults, setShowResults] = useState(false);
  const [resultData, setResultData] = useState(null);
  
  // Theme-based button styling
  const buttonStyles = {
    mint: "bg-emerald-500 hover:bg-emerald-600",
    pink: "bg-pink-500 hover:bg-pink-600",
    lavender: "bg-purple-500 hover:bg-purple-600"
  };

  // Financial Values Assessment questions
  const questions = [
    {
      id: "priority_spending",
      text: "When you have extra money, what are you most likely to do with it?",
      options: [
        { value: "save", label: "Save it for future security" },
        { value: "experience", label: "Spend it on an experience (travel, event, etc.)" },
        { value: "material", label: "Buy something tangible I've wanted" },
        { value: "debt", label: "Pay down existing debt" }
      ]
    },
    {
      id: "future_vs_present",
      text: "How do you balance future financial security with present enjoyment?",
      options: [
        { value: "future_strong", label: "Strong focus on future—I sacrifice now for later" },
        { value: "future_moderate", label: "Moderate focus on future—I save but enjoy now too" },
        { value: "present_moderate", label: "Moderate focus on present—I enjoy now but save some" },
        { value: "present_strong", label: "Strong focus on present—Why worry about a future that's uncertain?" }
      ]
    },
    {
      id: "purpose_money",
      text: "What do you believe is the primary purpose of money?",
      options: [
        { value: "security", label: "To provide security and stability" },
        { value: "freedom", label: "To enable freedom and choices" },
        { value: "enjoyment", label: "To enhance enjoyment of life" },
        { value: "status", label: "To achieve status and success" }
      ]
    },
    {
      id: "satisfaction_source",
      text: "Which purchase typically brings you the most lasting satisfaction?",
      options: [
        { value: "experiences", label: "Experiences (travel, concerts, activities)" },
        { value: "quality_goods", label: "High-quality, long-lasting goods" },
        { value: "gifts", label: "Gifts for others" },
        { value: "convenience", label: "Convenience or time-saving services" }
      ]
    },
    {
      id: "risk_approach",
      text: "How do you approach financial risk?",
      options: [
        { value: "avoidant", label: "I avoid risk whenever possible" },
        { value: "calculated", label: "I take calculated risks after research" },
        { value: "moderate", label: "I'm comfortable with moderate risk" },
        { value: "embracing", label: "I embrace risk as an opportunity" }
      ]
    },
    {
      id: "spending_regret",
      text: "When you feel regret about spending, it's usually because:",
      options: [
        { value: "waste", label: "The purchase wasn't used or appreciated as expected" },
        { value: "budget", label: "It strained my budget or savings goals" },
        { value: "alternative", label: "I could have spent the money on something better" },
        { value: "impulse", label: "It was an impulsive decision I didn't think through" }
      ]
    },
    {
      id: "savings_motivation",
      text: "What's your primary motivation for saving money?",
      options: [
        { value: "emergency", label: "Having an emergency fund for unexpected needs" },
        { value: "specific_goal", label: "Saving for specific large purchases or goals" },
        { value: "retirement", label: "Long-term security and retirement" },
        { value: "freedom", label: "Financial independence and freedom of choice" }
      ]
    },
    {
      id: "luxury_view",
      text: "How do you view luxury or premium purchases?",
      options: [
        { value: "waste", label: "Mostly a waste of money—function matters more than brand" },
        { value: "occasional", label: "Occasional treats that should be carefully chosen" },
        { value: "quality", label: "Worth it for the quality and durability" },
        { value: "deserved", label: "A deserved reward for hard work" }
      ]
    },
    {
      id: "income_increase",
      text: "If your income increased significantly, what would you do first?",
      options: [
        { value: "save_more", label: "Save or invest a larger percentage" },
        { value: "pay_debt", label: "Pay off all debts" },
        { value: "lifestyle", label: "Improve your lifestyle (better housing, car, etc.)" },
        { value: "give", label: "Give more to causes or people you care about" }
      ]
    },
    {
      id: "financial_success",
      text: "How do you primarily define financial success?",
      options: [
        { value: "freedom", label: "Having the freedom to make choices without financial constraints" },
        { value: "security", label: "Having enough security to never worry about basic needs" },
        { value: "wealth", label: "Building significant wealth and assets" },
        { value: "give", label: "Being able to be generous with others" }
      ]
    }
  ];
  
  // Handle option selection
  const handleSelect = (value) => {
    const updatedAnswers = { ...answers, [questions[currentQuestion].id]: value };
    setAnswers(updatedAnswers);
    
    if (currentQuestion < questions.length - 1) {
      // Move to next question
      setCurrentQuestion(currentQuestion + 1);
    } else {
      // Calculate results
      calculateResults(updatedAnswers);
    }
  };
  
  // Calculate assessment results
  const calculateResults = (finalAnswers) => {
    // Define financial values profiles with point systems
    const valuePoints = {
      "Security Seeker": 0,
      "Experience Collector": 0,
      "Quality Enthusiast": 0,
      "Freedom Pursuer": 0,
      "Balanced Pragmatist": 0,
      "Social Impact Investor": 0,
      "Status Achiever": 0
    };
    
    // Score based on priority spending
    if (finalAnswers.priority_spending === "save") {
      valuePoints["Security Seeker"] += 3;
      valuePoints["Balanced Pragmatist"] += 1;
    } else if (finalAnswers.priority_spending === "experience") {
      valuePoints["Experience Collector"] += 3;
      valuePoints["Freedom Pursuer"] += 1;
    } else if (finalAnswers.priority_spending === "material") {
      valuePoints["Quality Enthusiast"] += 2;
      valuePoints["Status Achiever"] += 2;
    } else if (finalAnswers.priority_spending === "debt") {
      valuePoints["Security Seeker"] += 2;
      valuePoints["Balanced Pragmatist"] += 2;
    }
    
    // Score based on future vs present
    if (finalAnswers.future_vs_present === "future_strong") {
      valuePoints["Security Seeker"] += 3;
      valuePoints["Status Achiever"] += 1;
    } else if (finalAnswers.future_vs_present === "future_moderate") {
      valuePoints["Balanced Pragmatist"] += 3;
      valuePoints["Security Seeker"] += 1;
    } else if (finalAnswers.future_vs_present === "present_moderate") {
      valuePoints["Balanced Pragmatist"] += 2;
      valuePoints["Freedom Pursuer"] += 2;
    } else if (finalAnswers.future_vs_present === "present_strong") {
      valuePoints["Experience Collector"] += 3;
      valuePoints["Freedom Pursuer"] += 1;
    }
    
    // Score based on purpose of money
    if (finalAnswers.purpose_money === "security") {
      valuePoints["Security Seeker"] += 3;
      valuePoints["Balanced Pragmatist"] += 1;
    } else if (finalAnswers.purpose_money === "freedom") {
      valuePoints["Freedom Pursuer"] += 3;
      valuePoints["Experience Collector"] += 1;
    } else if (finalAnswers.purpose_money === "enjoyment") {
      valuePoints["Experience Collector"] += 2;
      valuePoints["Quality Enthusiast"] += 2;
    } else if (finalAnswers.purpose_money === "status") {
      valuePoints["Status Achiever"] += 3;
      valuePoints["Quality Enthusiast"] += 1;
    }
    
    // Score based on satisfaction source
    if (finalAnswers.satisfaction_source === "experiences") {
      valuePoints["Experience Collector"] += 3;
      valuePoints["Freedom Pursuer"] += 1;
    } else if (finalAnswers.satisfaction_source === "quality_goods") {
      valuePoints["Quality Enthusiast"] += 3;
      valuePoints["Status Achiever"] += 1;
    } else if (finalAnswers.satisfaction_source === "gifts") {
      valuePoints["Social Impact Investor"] += 3;
      valuePoints["Freedom Pursuer"] += 1;
    } else if (finalAnswers.satisfaction_source === "convenience") {
      valuePoints["Freedom Pursuer"] += 2;
      valuePoints["Balanced Pragmatist"] += 2;
    }
    
    // Score based on risk approach
    if (finalAnswers.risk_approach === "avoidant") {
      valuePoints["Security Seeker"] += 3;
      valuePoints["Balanced Pragmatist"] += 1;
    } else if (finalAnswers.risk_approach === "calculated") {
      valuePoints["Balanced Pragmatist"] += 3;
      valuePoints["Quality Enthusiast"] += 1;
    } else if (finalAnswers.risk_approach === "moderate") {
      valuePoints["Freedom Pursuer"] += 2;
      valuePoints["Experience Collector"] += 2;
    } else if (finalAnswers.risk_approach === "embracing") {
      valuePoints["Status Achiever"] += 2;
      valuePoints["Experience Collector"] += 2;
    }
    
    // Score based on spending regret
    if (finalAnswers.spending_regret === "waste") {
      valuePoints["Quality Enthusiast"] += 3;
      valuePoints["Balanced Pragmatist"] += 1;
    } else if (finalAnswers.spending_regret === "budget") {
      valuePoints["Security Seeker"] += 3;
      valuePoints["Balanced Pragmatist"] += 1;
    } else if (finalAnswers.spending_regret === "alternative") {
      valuePoints["Freedom Pursuer"] += 2;
      valuePoints["Experience Collector"] += 2;
    } else if (finalAnswers.spending_regret === "impulse") {
      valuePoints["Balanced Pragmatist"] += 2;
      valuePoints["Security Seeker"] += 2;
    }
    
    // Score based on savings motivation
    if (finalAnswers.savings_motivation === "emergency") {
      valuePoints["Security Seeker"] += 3;
      valuePoints["Balanced Pragmatist"] += 1;
    } else if (finalAnswers.savings_motivation === "specific_goal") {
      valuePoints["Quality Enthusiast"] += 2;
      valuePoints["Experience Collector"] += 2;
    } else if (finalAnswers.savings_motivation === "retirement") {
      valuePoints["Security Seeker"] += 2;
      valuePoints["Balanced Pragmatist"] += 2;
    } else if (finalAnswers.savings_motivation === "freedom") {
      valuePoints["Freedom Pursuer"] += 3;
      valuePoints["Experience Collector"] += 1;
    }
    
    // Score based on luxury view
    if (finalAnswers.luxury_view === "waste") {
      valuePoints["Security Seeker"] += 2;
      valuePoints["Balanced Pragmatist"] += 2;
    } else if (finalAnswers.luxury_view === "occasional") {
      valuePoints["Balanced Pragmatist"] += 3;
      valuePoints["Freedom Pursuer"] += 1;
    } else if (finalAnswers.luxury_view === "quality") {
      valuePoints["Quality Enthusiast"] += 3;
      valuePoints["Status Achiever"] += 1;
    } else if (finalAnswers.luxury_view === "deserved") {
      valuePoints["Status Achiever"] += 3;
      valuePoints["Experience Collector"] += 1;
    }
    
    // Score based on income increase
    if (finalAnswers.income_increase === "save_more") {
      valuePoints["Security Seeker"] += 3;
      valuePoints["Balanced Pragmatist"] += 1;
    } else if (finalAnswers.income_increase === "pay_debt") {
      valuePoints["Security Seeker"] += 2;
      valuePoints["Balanced Pragmatist"] += 2;
    } else if (finalAnswers.income_increase === "lifestyle") {
      valuePoints["Status Achiever"] += 2;
      valuePoints["Quality Enthusiast"] += 2;
    } else if (finalAnswers.income_increase === "give") {
      valuePoints["Social Impact Investor"] += 3;
      valuePoints["Freedom Pursuer"] += 1;
    }
    
    // Score based on financial success
    if (finalAnswers.financial_success === "freedom") {
      valuePoints["Freedom Pursuer"] += 3;
      valuePoints["Experience Collector"] += 1;
    } else if (finalAnswers.financial_success === "security") {
      valuePoints["Security Seeker"] += 3;
      valuePoints["Balanced Pragmatist"] += 1;
    } else if (finalAnswers.financial_success === "wealth") {
      valuePoints["Status Achiever"] += 3;
      valuePoints["Quality Enthusiast"] += 1;
    } else if (finalAnswers.financial_success === "give") {
      valuePoints["Social Impact Investor"] += 3;
      valuePoints["Freedom Pursuer"] += 1;
    }
    
    // Find top value profile
    const primaryValue = Object.entries(valuePoints)
      .sort((a, b) => b[1] - a[1])[0][0];
    
    // Find secondary value profile
    const secondaryValue = Object.entries(valuePoints)
      .sort((a, b) => b[1] - a[1])[1][0];
    
    // Prepare detailed result data
    const valueProfiles = {
      "Security Seeker": {
        description: "You prioritize financial stability and security above all else. You're cautious with your money and focus on building a strong foundation of savings and low-risk investments.",
        emoji: "🛡️",
        strengths: [
          "Excellent at avoiding financial crises",
          "Disciplined saver and budget-keeper",
          "Thoughtful and careful with major financial decisions",
          "Likely to achieve long-term financial security"
        ],
        challenges: [
          "May miss opportunities due to caution",
          "Could over-prioritize future security at expense of present enjoyment",
          "May experience anxiety around financial risks",
          "Might struggle to spend on 'non-essentials' even when affordable"
        ],
        shopping_tips: [
          "Create a 'fun money' allocation in your budget that you can spend without guilt",
          "Consider value in terms of longevity and reliability, not just price",
          "Research major purchases thoroughly to boost confidence in your decisions",
          "Balance your natural caution with occasional calculated risks"
        ],
        spending_impact: "Your cautious approach helps you avoid debt and build security, but may sometimes limit experiences. Finding balance by allowing some calculated 'fun spending' could enhance your financial well-being."
      },
      "Experience Collector": {
        description: "You value experiences over material possessions and see money as a tool to create memorable moments and adventures in life.",
        emoji: "🧳",
        strengths: [
          "Likely to accumulate rich life experiences and memories",
          "Less prone to clutter and material excess",
          "Often highly adaptable with spending priorities",
          "Generally experience less buyer's remorse for meaningful experiences"
        ],
        challenges: [
          "May underinvest in long-term financial security",
          "Could overspend on experiences that don't deliver expected value",
          "Might struggle with consistent saving habits",
          "Sometimes neglects practical needs for exciting opportunities"
        ],
        shopping_tips: [
          "Create a dedicated 'experience fund' with regular contributions",
          "Research experiences thoroughly to ensure they align with your values",
          "Balance spontaneity with some financial planning",
          "Consider lower-cost experiences that might deliver similar satisfaction"
        ],
        spending_impact: "Your focus on experiences often leads to lasting memories and life satisfaction, but may sometimes leave practical needs underfunded. Creating a balanced approach with dedicated experience savings can help you enjoy both adventure and security."
      },
      "Quality Enthusiast": {
        description: "You believe in investing in quality and are willing to pay more for items that will last longer, perform better, or provide superior experiences.",
        emoji: "✨",
        strengths: [
          "Purchases tend to have longevity and durability",
          "Usually gets good value over the lifetime of products",
          "Less likely to make frequent replacements of items",
          "Tends to research purchases thoroughly"
        ],
        challenges: [
          "May overspend on premium features that don't add proportional value",
          "Sometimes uses 'quality' to justify unnecessary purchases",
          "Could accumulate expensive items beyond actual needs",
          "Might experience more financial strain for 'buy it for life' items"
        ],
        shopping_tips: [
          "Always question whether premium features justify the price increase",
          "Focus quality investments on items you use daily or frequently",
          "Research diminishing returns in product categories",
          "Create a priority list for quality upgrades rather than pursuing all at once"
        ],
        spending_impact: "Your appreciation for quality often leads to better long-term value, but can sometimes strain short-term finances or justify unnecessary purchases. Being selective about which categories deserve premium investment can optimize your approach."
      },
      "Freedom Pursuer": {
        description: "You view money primarily as a tool for creating freedom, options, and independence in your life, rather than for security or status.",
        emoji: "🦅",
        strengths: [
          "Makes financial decisions that expand life options",
          "Often willing to take calculated risks for greater returns",
          "Values flexibility and adaptability in financial planning",
          "Typically balances present enjoyment with future needs"
        ],
        challenges: [
          "May prioritize optionality over commitment in ways that increase costs",
          "Sometimes underestimates the value of stability",
          "Could make impulsive decisions in pursuit of perceived freedom",
          "Might resist financial structures that feel constraining"
        ],
        shopping_tips: [
          "Evaluate purchases based on whether they expand or limit your future choices",
          "Invest in tools and resources that increase your capabilities",
          "Be wary of subscriptions or commitments that reduce financial flexibility",
          "Balance 'freedom to' purchases with maintaining 'freedom from' financial stress"
        ],
        spending_impact: "Your focus on freedom leads to an expansive, opportunity-filled life, but may sometimes create financial complexity or overlook the value of stability. Creating systems that preserve flexibility while building security can enhance your approach."
      },
      "Balanced Pragmatist": {
        description: "You take a moderate, practical approach to money, seeking a careful balance between enjoying the present and preparing for the future.",
        emoji: "⚖️",
        strengths: [
          "Makes thoughtful trade-offs between competing financial priorities",
          "Usually avoids financial extremes or reckless decisions",
          "Adaptable and reasonable with changing financial circumstances",
          "Generally experiences moderate but consistent financial progress"
        ],
        challenges: [
          "May sometimes lack the conviction to fully commit to financial goals",
          "Could miss opportunities that require bold action",
          "Might settle for 'good enough' rather than optimizing financial choices",
          "Sometimes lacks a clear financial identity or direction"
        ],
        shopping_tips: [
          "Create clear guidelines for when to save vs. when to splurge",
          "Develop a personal 'financial philosophy' to guide decisions",
          "Set up automated systems to ensure progress toward long-term goals",
          "Periodically review whether your balanced approach is achieving your true priorities"
        ],
        spending_impact: "Your balanced approach helps you avoid major financial mistakes and maintain steady progress, but may sometimes lack the focus needed for transformational outcomes. Adding some deliberate emphasis to your highest-priority goals could enhance your financial trajectory."
      },
      "Social Impact Investor": {
        description: "You see money as a tool for creating positive change in the world and in the lives of others, not just for personal benefit.",
        emoji: "🌱",
        strengths: [
          "Makes financial decisions aligned with personal values and ethics",
          "Often experiences deep satisfaction from generous or impact-focused spending",
          "Typically conscious of the broader implications of financial choices",
          "Generally less focused on material accumulation for its own sake"
        ],
        challenges: [
          "May sometimes neglect personal financial needs in favor of others",
          "Could fall prey to emotional appeals that aren't truly impactful",
          "Might struggle to balance generosity with necessary self-care",
          "Sometimes experiences conflict between values and financial optimization"
        ],
        shopping_tips: [
          "Research the actual impact of 'socially conscious' purchases",
          "Create a dedicated 'giving fund' to balance generosity with personal needs",
          "Consider how your everyday purchases align with your values",
          "Remember that your own financial security enables sustained positive impact"
        ],
        spending_impact: "Your focus on impact and values creates meaning in your financial life, but may sometimes come at the expense of personal financial security. Finding the balance that allows you to create sustained impact over time will maximize your positive influence."
      },
      "Status Achiever": {
        description: "You view money partly as a way to signal success, achievement, and discerning taste to yourself and others.",
        emoji: "🏆",
        strengths: [
          "Often highly motivated to increase income and financial achievement",
          "Typically appreciates quality and excellence in purchases",
          "Usually well-informed about premium options in categories of interest",
          "Frequently willing to work hard for financial advancement"
        ],
        challenges: [
          "May spend disproportionately on visible status markers",
          "Could feel pressured to maintain appearances beyond financial comfort",
          "Might base purchase decisions on external perceptions rather than personal value",
          "Sometimes experiences a hedonic treadmill of always wanting more"
        ],
        shopping_tips: [
          "Question whether purchases are for your satisfaction or others' perceptions",
          "Look for quality indicators beyond brand name or price",
          "Set clear limits on status spending as a percentage of income",
          "Consider whether experiences might deliver more satisfaction than status goods"
        ],
        spending_impact: "Your appreciation for achievement and quality often leads to a motivated financial approach, but may sometimes prioritize appearance over substance. Focusing on personally meaningful success markers rather than external validation can enhance your financial and emotional well-being."
      }
    };
    
    // Calculate trait percentages based on points
    const totalPoints = Object.values(valuePoints).reduce((sum, points) => sum + points, 0);
    
    const traitPercentages = {};
    Object.entries(valuePoints).forEach(([trait, points]) => {
      traitPercentages[trait] = Math.round((points / totalPoints) * 100);
    });
    
    // Prepare final result data
    setResultData({
      primaryValue,
      secondaryValue,
      description: valueProfiles[primaryValue].description,
      emoji: valueProfiles[primaryValue].emoji,
      strengths: valueProfiles[primaryValue].strengths,
      challenges: valueProfiles[primaryValue].challenges,
      shopping_tips: valueProfiles[primaryValue].shopping_tips,
      spending_impact: valueProfiles[primaryValue].spending_impact,
      traitPercentages: traitPercentages,
      secondaryDescription: `You also strongly identify with the ${secondaryValue} financial value system.`
    });
    
    // Show results
    setShowResults(true);
  };
  
  // Go back to previous question
  const handlePrevious = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(currentQuestion - 1);
    }
  };
  
  // Restart the assessment
  const handleRestart = () => {
    setCurrentQuestion(0);
    setAnswers({});
    setShowResults(false);
    setResultData(null);
  };
  
  // Prepare data for ShareStatsCard component
  const prepareShareData = () => {
    if (!resultData) return null;
    
    return {
      quizTitle: "Financial Values Assessment",
      result: resultData.primaryValue,
      resultEmoji: resultData.emoji,
      resultDescription: resultData.description,
      resultTagline: "Understanding your financial values is the first step to mindful spending!",
      quizType: "financial_values"
    };
  };
  
  return (
    <motion.div 
      className="bg-white dark:bg-gray-800 rounded-3xl shadow-xl p-6 mb-8"
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.3 }}
    >
      {!showResults ? (
        <>
          <div className="text-center mb-6">
            <h1 className="text-2xl font-bold mb-3 dark:text-white">Financial Values Assessment</h1>
            <p className="text-gray-600 dark:text-gray-300">
              Discover your financial values and understand how they influence your spending decisions.
            </p>
          </div>
          
          {/* Progress bar */}
          <div className="mb-6">
            <div className="flex justify-between text-sm text-gray-600 dark:text-gray-400 mb-1">
              <span>Question {currentQuestion + 1} of {questions.length}</span>
              <span>{Math.round((currentQuestion / questions.length) * 100)}% Complete</span>
            </div>
            <Progress
              value={(currentQuestion / questions.length) * 100}
              className="h-2 bg-gray-200 dark:bg-gray-700"
              style={{
                "--progress-indicator-color": 
                  theme === "mint" ? "#10b981" : 
                  theme === "pink" ? "#ec4899" : 
                  "#8b5cf6" // lavender
              }}
            />
          </div>
          
          {/* Current question */}
          <div className="mb-8">
            <h2 className="text-xl font-semibold mb-4 dark:text-white">
              {questions[currentQuestion].text}
            </h2>
            
            <div className="space-y-3">
              {questions[currentQuestion].options.map((option) => (
                <motion.button
                  key={option.value}
                  onClick={() => handleSelect(option.value)}
                  className={`w-full py-3 px-4 text-left rounded-xl border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 transition ${
                    answers[questions[currentQuestion].id] === option.value
                      ? `border-2 border-${theme}-500 dark:border-${theme}-400`
                      : ""
                  }`}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  {option.label}
                </motion.button>
              ))}
            </div>
          </div>
          
          {/* Navigation buttons */}
          <div className="flex justify-between">
            <motion.button
              onClick={handlePrevious}
              disabled={currentQuestion === 0}
              className={`px-4 py-2 rounded-xl border border-gray-300 dark:border-gray-600 ${
                currentQuestion === 0 ? "opacity-50 cursor-not-allowed" : ""
              }`}
              whileHover={currentQuestion !== 0 ? { scale: 1.05 } : {}}
              whileTap={currentQuestion !== 0 ? { scale: 0.95 } : {}}
            >
              Previous
            </motion.button>
            
            <div className="text-sm text-gray-500 dark:text-gray-400 self-center">
              Your results are private and stored only on your device.
            </div>
          </div>
        </>
      ) : (
        <div>
          <div className="text-center mb-6">
            <h1 className="text-2xl font-bold mb-2 dark:text-white">Your Financial Values</h1>
            <div className="text-6xl mb-3">{resultData.emoji}</div>
            <h2 className="text-xl font-bold mb-1 dark:text-white">{resultData.primaryValue}</h2>
            <p className="text-gray-600 dark:text-gray-300 mb-1">
              {resultData.description}
            </p>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {resultData.secondaryDescription}
            </p>
          </div>
          
          {/* Value Profile Breakdown */}
          <div className="mb-6">
            <h3 className="font-semibold mb-3 dark:text-white">Your Values Breakdown</h3>
            <div className="space-y-2">
              {Object.entries(resultData.traitPercentages)
                .sort(([, a], [, b]) => b - a)
                .map(([trait, percentage]) => (
                  <div key={trait} className="bg-gray-50 dark:bg-gray-700 rounded-lg p-2">
                    <div className="flex justify-between mb-1">
                      <span className="text-sm font-medium dark:text-white">{trait}</span>
                      <span className="text-sm text-gray-600 dark:text-gray-400">{percentage}%</span>
                    </div>
                    <div className="w-full bg-gray-200 dark:bg-gray-600 h-2 rounded-full overflow-hidden">
                      <div 
                        className={`h-full ${
                          trait === resultData.primaryValue
                            ? "bg-blue-500"
                            : trait === resultData.secondaryValue
                            ? "bg-purple-500"
                            : "bg-gray-400 dark:bg-gray-500"
                        }`}
                        style={{ width: `${percentage}%` }}
                      ></div>
                    </div>
                  </div>
                ))
              }
            </div>
          </div>
          
          {/* Strengths & Challenges */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <div className="bg-green-50 dark:bg-green-900/20 rounded-xl p-4">
              <h3 className="font-semibold mb-3 flex items-center dark:text-white">
                <span className="mr-2">💪</span>
                Financial Strengths
              </h3>
              <ul className="space-y-2 text-gray-700 dark:text-gray-300">
                {resultData.strengths.map((strength, index) => (
                  <li key={index} className="flex items-start">
                    <span className="text-green-500 mr-2">•</span> {strength}
                  </li>
                ))}
              </ul>
            </div>
            
            <div className="bg-amber-50 dark:bg-amber-900/20 rounded-xl p-4">
              <h3 className="font-semibold mb-3 flex items-center dark:text-white">
                <span className="mr-2">⚠️</span> Financial Challenges
              </h3>
              <ul className="space-y-2 text-gray-700 dark:text-gray-300">
                {resultData.challenges.map((challenge, index) => (
                  <li key={index} className="flex items-start">
                    <span className="text-amber-500 mr-2">•</span> {challenge}
                  </li>
                ))}
              </ul>
            </div>
          </div>
          
          {/* Shopping Tips */}
          <div className="bg-blue-50 dark:bg-blue-900/20 rounded-xl p-4 mb-6">
            <h3 className="font-semibold mb-3 flex items-center dark:text-white">
              <span className="mr-2">💡</span> Personalized Spending Tips
            </h3>
            <div className="space-y-3">
              {resultData.shopping_tips.map((tip, index) => (
                <div key={index} className="flex items-start">
                  <div className="bg-white dark:bg-gray-800 text-blue-500 rounded-full h-5 w-5 flex items-center justify-center text-xs font-bold mr-2 mt-0.5">
                    {index + 1}
                  </div>
                  <div className="text-sm text-gray-700 dark:text-gray-300">{tip}</div>
                </div>
              ))}
            </div>
          </div>
          
          {/* Spending Impact */}
          <div className="bg-purple-50 dark:bg-purple-900/20 rounded-xl p-4 mb-6">
            <h3 className="font-semibold mb-2 flex items-center dark:text-white">
              <span className="mr-2">💰</span> Your Values & Spending
            </h3>
            <p className="text-sm text-gray-700 dark:text-gray-300">
              {resultData.spending_impact}
            </p>
          </div>
          
          {/* Share Results Card */}
          <ShareStatsCard 
            purchaseData={prepareShareData()}
            theme={theme}
            userPersonality={resultData.primaryValue}
            cardType="quiz"
          />

          <ShareableInfographics
            purchaseData={prepareShareData()}
            theme={theme}
            userPersonality={resultData.primaryType}
            cardType="quiz"
          />

          {/* Shop Recommendations */}
          {showResults && (
            <div className="bg-green-50 dark:bg-green-900/20 rounded-xl p-4 mb-6">
              <h3 className="font-semibold mb-2 dark:text-white flex items-center">
                <span className="mr-2">💰</span> Value-Aligned Products
              </h3>
              <p className="text-sm text-gray-700 dark:text-gray-300 mb-3">
                {resultData.primaryValue === "Security Seeker" ? 
                  "Discover tools that enhance financial stability and help you maintain your security-focused approach to spending." :
                resultData.primaryValue === "Experience Collector" ?
                  "Find resources that maximize the value of your experiences while keeping spending mindful and intentional." :
                resultData.primaryValue === "Quality Enthusiast" ?
                  "Browse our selection of high-value, long-lasting products that align with your commitment to quality investments." :
                resultData.primaryValue === "Freedom Pursuer" ?
                  "Explore resources that expand your financial options while preserving your freedom-focused approach to money." :
                resultData.primaryValue === "Balanced Pragmatist" ?
                  "See our curated selection of practical tools that support your balanced approach to spending and saving." :
                resultData.primaryValue === "Social Impact Investor" ?
                  "Discover products with positive social impact that align with your values-driven financial approach." :
                resultData.primaryValue === "Status Achiever" ?
                  "Find quality alternatives that deliver genuine value while satisfying your appreciation for excellence." :
                  "Browse products tailored to your financial values that encourage mindful spending practices."
                }
              </p>
              <motion.button
                onClick={() => window.location.href = '/shop'}
                className={`px-4 py-2 w-full rounded-lg text-white text-sm ${buttonStyles[theme]}`}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                View Value-Aligned Recommendations
              </motion.button>
            </div>
          )}
          
          {/* Action buttons */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-6">
            <motion.button
              onClick={handleRestart}
              className="py-3 rounded-xl border border-gray-300 dark:border-gray-600 font-semibold"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              Take Quiz Again
            </motion.button>
            
            <motion.button
              onClick={goToInput}
              className={`py-3 rounded-xl text-white font-semibold ${buttonStyles[theme]}`}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              Track Your Purchases
            </motion.button>
          </div>
          
          <div className="mt-4 text-center">
            <p className="text-xs text-gray-500 dark:text-gray-400">
              This quiz is for informational purposes only. Financial values exist on a spectrum and may evolve over time.
            </p>
          </div>
        </div>
      )}
    </motion.div>
  );
};

export default FinancialValuesQuiz;
